import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import type { ReleaseNote } from '@trackly/shared';

interface Props {
  open: boolean;
  notes: ReleaseNote[];
  /** Ids of the notes that were unseen when the dialog opened. */
  newIds: string[];
  onClose: () => void;
}

function formatDate(date: string) {
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

const WhatsNewDialog = ({ open, notes, newIds, onClose }: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      transitionDuration={reduceMotion ? 0 : undefined}
      aria-labelledby="whats-new-title"
    >
      <DialogTitle id="whats-new-title">What's new</DialogTitle>
      <DialogContent dividers>
        {notes.length === 0 ? (
          <Typography color="text.secondary">No updates yet.</Typography>
        ) : (
          <Stack component="ul" spacing={3} sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {notes.map((note) => (
              <li key={note.id}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" component="h3" fontWeight={600}>
                    {note.title}
                  </Typography>
                  {newIds.includes(note.id) && <Chip label="New" color="primary" size="small" />}
                </Stack>
                <Typography variant="caption" color="text.secondary" component="time" dateTime={note.date}>
                  {formatDate(note.date)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                  {note.body}
                </Typography>
              </li>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsNewDialog;
